import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-foreground font-bold text-xl hover:opacity-80 transition-opacity">
            <svg className="w-7 h-7 text-primary" fill="currentColor" viewBox="0 0 24 24">
              <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
            </svg>
            TabKeeper
          </Link>
          <Link to="/" className="text-primary hover:text-primary/80 font-medium transition-colors">
            ← Voltar ao início
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-extrabold text-foreground mb-2">Política de Privacidade</h1>
        <p className="text-muted-foreground mb-8 pb-8 border-b border-border">
          Última atualização: 21 de fevereiro de 2026
        </p>

        <div className="bg-card rounded-2xl p-8 shadow-sm border border-border space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">1. Introdução</h2>
            <p className="text-foreground/90 mb-4">
              O TabKeeper ("nós", "nosso" ou "Aplicativo") está comprometido em proteger sua privacidade. 
              Esta Política de Privacidade explica como coletamos, usamos, armazenamos e protegemos suas 
              informações pessoais quando você utiliza nosso serviço de gerenciamento de favoritos.
            </p>
            <div className="bg-primary/5 border-l-4 border-primary p-4 rounded-r-lg">
              <strong className="text-foreground">Resumo:</strong>
              <span className="text-foreground/80"> Coletamos apenas os dados necessários para fornecer o serviço. 
              Seus favoritos são seus e você tem controle total sobre eles.</span>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">2. Informações que Coletamos</h2>
            <p className="text-foreground/90 mb-3">Coletamos as seguintes categorias de informações:</p>
            <ul className="list-disc pl-6 space-y-2 text-foreground/90">
              <li><strong>Informações de Conta:</strong> E-mail, nome e senha criptografada quando você cria uma conta.</li>
              <li><strong>Dados de Autenticação Google:</strong> Se você optar por login via Google, recebemos seu e-mail e nome do perfil Google.</li>
              <li><strong>Favoritos e Organizações:</strong> URLs, títulos, categorias e tags que você salva no aplicativo.</li>
              <li><strong>Dados de Uso:</strong> Informações sobre como você interage com o aplicativo (funcionalidades utilizadas, frequência de acesso).</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">3. Como Usamos Suas Informações</h2>
            <p className="text-foreground/90 mb-3">Utilizamos suas informações para:</p>
            <ul className="list-disc pl-6 space-y-2 text-foreground/90">
              <li>Fornecer, manter e melhorar o serviço TabKeeper</li>
              <li>Sincronizar seus favoritos entre dispositivos</li>
              <li>Autenticar seu acesso e proteger sua conta</li>
              <li>Enviar comunicações importantes sobre o serviço (opcional)</li>
              <li>Analisar padrões de uso para melhorar a experiência do usuário</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">4. Armazenamento e Segurança</h2>
            <p className="text-foreground/90 mb-3">Seus dados são armazenados em servidores seguros com as seguintes proteções:</p>
            <ul className="list-disc pl-6 space-y-2 text-foreground/90">
              <li>Criptografia de senhas usando algoritmos modernos (bcrypt)</li>
              <li>Conexões seguras via HTTPS</li>
              <li>Tokens de sessão com expiração automática</li>
              <li>Backups regulares e redundância de dados</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">5. Compartilhamento de Dados</h2>
            <p className="text-foreground/90 mb-3">
              <strong>Não vendemos, alugamos ou compartilhamos suas informações pessoais com terceiros</strong>, 
              exceto nas seguintes circunstâncias:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-foreground/90">
              <li>Quando exigido por lei ou ordem judicial</li>
              <li>Para proteger nossos direitos legais ou segurança dos usuários</li>
              <li>Com seu consentimento explícito</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">6. Seus Direitos</h2>
            <p className="text-foreground/90 mb-3">Você tem o direito de:</p>
            <ul className="list-disc pl-6 space-y-2 text-foreground/90">
              <li><strong>Acessar:</strong> Visualizar todos os dados que mantemos sobre você</li>
              <li><strong>Exportar:</strong> Baixar seus favoritos a qualquer momento</li>
              <li><strong>Corrigir:</strong> Atualizar ou corrigir suas informações</li>
              <li><strong>Excluir:</strong> Solicitar a exclusão completa de sua conta e dados</li>
              <li><strong>Portabilidade:</strong> Transferir seus dados para outro serviço</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">7. Cookies e Tecnologias Similares</h2>
            <p className="text-foreground/90 mb-3">Utilizamos cookies essenciais para:</p>
            <ul className="list-disc pl-6 space-y-2 text-foreground/90">
              <li>Manter sua sessão de login ativa</li>
              <li>Lembrar suas preferências (tema, idioma)</li>
              <li>Garantir a segurança da aplicação</li>
            </ul>
            <p className="text-foreground/90 mt-3">Não utilizamos cookies de rastreamento ou publicidade.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">8. Retenção de Dados</h2>
            <p className="text-foreground/90 mb-3">Mantemos seus dados enquanto sua conta estiver ativa. Após a exclusão da conta:</p>
            <ul className="list-disc pl-6 space-y-2 text-foreground/90">
              <li>Dados pessoais são removidos em até 30 dias</li>
              <li>Backups são eliminados em até 90 dias</li>
              <li>Logs anonimizados podem ser mantidos para análise</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">9. Alterações nesta Política</h2>
            <p className="text-foreground/90">
              Podemos atualizar esta política periodicamente. Notificaremos você sobre alterações significativas 
              por e-mail ou através do aplicativo. O uso continuado do serviço após as alterações constitui 
              aceitação da política atualizada.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">10. Contato</h2>
            <p className="text-foreground/90 mb-3">Para questões sobre privacidade ou para exercer seus direitos, entre em contato:</p>
            <ul className="list-disc pl-6 space-y-2 text-foreground/90">
              <li><strong>E-mail:</strong> privacidade@tabkeeper.app</li>
            </ul>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="text-center py-8 text-muted-foreground text-sm">
        <p>© 2026 TabKeeper. Todos os direitos reservados.</p>
        <p className="mt-2">
          <Link to="/privacidade" className="text-primary hover:underline">Privacidade</Link>
          {' · '}
          <Link to="/termos" className="text-primary hover:underline">Termos de Serviço</Link>
        </p>
      </footer>
    </div>
  );
};

export default PrivacyPage;
